/* eslint-disable no-console */
import React, { useState } from 'react';
import format from 'date-fns/format';
import { string, object, arrayOf, func, bool } from 'prop-types';
import { compose } from 'redux';
import { get } from 'lodash';
import moment from 'moment-timezone';
import classNames from 'classnames';

import { injectIntl } from 'react-intl';
import config from '../../config';
import { types as sdkTypes } from '../../util/sdkLoader';

import { daysBetween } from '../../util/dates';
import { ensureUser, ensureStripeCustomer, ensurePaymentMethodCard } from '../../util/data';
import {
  getBookingDates,
  calculateBookingChangesPrices,
  formatDatesForTransaction,
  HEADING_CUSTOMER_CHANGES_REQUESTED,
  getPackageFromTransaction,
} from '../../util/transaction';
import { formatMoney } from '../../util/currency';
import { getListingTimezone } from '../../util/listing';
import { propTypes } from '../../util/types';
import { TransactionListingHeading } from '..';
import Header from './Header';
import BookingDetails from './BookingDetails';
import BookingChangesErrorBoundary from './BookingChangesErrorBoundary';
import InlinePaymentElement from './InlinePaymentElement';
import PricingDetails from './PricingDetails';
import useInlinePaymentIntent from './hooks/useInlinePaymentIntent';

import css from './BookingChanges.module.css';
import { getFirstName } from '../../util/user';

const { Money } = sdkTypes;

const BookingChanges = ({
  currentListing,
  currentUser,
  currentTransaction,
  listingLink,
  packageId,
  timeSlots,
  onSubmit,
  onCompleteRequestChanges,
  onCreatePaymentIntent,
  inProgress,
  stateData,
  isProvider,
  isCustomer,
  onClose,
  onAccept,
  acceptChangesInProgress,
  onDecline,
  declineChangesInProgress,
  intl,
}) => {
  const [errorMessage, setErrorMessage] = useState(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const { customer } = currentTransaction;
  const { provider } = currentTransaction;

  const providedDisplayName = getFirstName(get(provider, 'attributes.profile.displayName', null));
  const customerDisplayName = getFirstName(get(customer, 'attributes.profile.displayName', null));
  const hasCustomerRequestedChanges = stateData.headingState === HEADING_CUSTOMER_CHANGES_REQUESTED;

  const currentProvider = ensureUser(currentTransaction.provider);
  const currentPackage = getPackageFromTransaction(currentListing, packageId, currentTransaction);

  const originalCustomerPrice = currentTransaction.attributes.payinTotal;
  const formattedCustomerOriginalPrice = formatMoney(intl, originalCustomerPrice);
  const originalProviderPrice = currentTransaction.attributes.payoutTotal;
  const formattedProviderOriginalPrice = formatMoney(intl, originalProviderPrice);

  const timezone = getListingTimezone(currentListing);

  const guestSize = get(
    currentTransaction,
    'attributes.protectedData.packageLineItem.guestSize',
    1
  );

  const flexibleGuestSize = get(
    currentTransaction,
    'attributes.protectedData.flexibleGuestSize',
    null
  );

  let totalGuestSize = guestSize;

  if (flexibleGuestSize) {
    totalGuestSize = { flexibleGuestSize };
  }

  const { bookingStart, bookingEnd, lodgingStartDate } = getBookingDates(currentTransaction);

  const [newGuestSize, setNewGuestSize] = useState(totalGuestSize);
  const [newBookingDates, setNewBookingDates] = useState({
    startDate: bookingStart.toDate(),
    endDate: bookingEnd.toDate(),
    startLodgingDate: lodgingStartDate?.toDate(),
  });

  const { newCustomerPrice, newProviderPrice, updatedLineItems } = calculateBookingChangesPrices(
    currentPackage,
    currentTransaction,
    newGuestSize,
    newBookingDates,
    hasCustomerRequestedChanges
  );

  const formattedNewCustomerPrice = formatMoney(intl, new Money(newCustomerPrice, config.currency));
  const formattedNewProviderPrice = formatMoney(intl, new Money(newProviderPrice, config.currency));

  const priceDifferenceForCustomer = new Money(
    newCustomerPrice - originalCustomerPrice.amount,
    config.currency
  );
  const priceDifferenceForProvider = new Money(
    newProviderPrice - originalProviderPrice.amount,
    config.currency
  );

  const formattedPriceDifferenceForCustomer = formatMoney(intl, priceDifferenceForCustomer);
  const formattedPriceDifferenceForProvider = formatMoney(intl, priceDifferenceForProvider);

  const hasPaymentMethod = !!(
    ensureStripeCustomer(currentUser.stripeCustomer).attributes.stripeCustomerId &&
    ensurePaymentMethodCard(currentUser.stripeCustomer?.defaultPaymentMethod).id
  );

  const needsInlinePayment =
    isCustomer &&
    !hasCustomerRequestedChanges &&
    !isProvider &&
    !hasPaymentMethod &&
    priceDifferenceForCustomer.amount > 0;

  const {
    paymentState,
    isCreatingPI,
    paymentError,
    isAwaitingInlinePayment,
    clearPaymentState,
  } = useInlinePaymentIntent({
    needsInlinePayment,
    onCreatePaymentIntent,
    currentUser,
    currentListing,
    currentProvider,
    updatedLineItems,
    transactionId: currentTransaction.id,
  });

  const combinedErrorMessage =
    errorMessage ||
    (paymentError ? intl.formatMessage({ id: 'BookingChanges.sendRequestError' }) : null);

  const buildSubmitParams = (values, initialValues) => {
    const partySizeChanged = values.partySize !== initialValues.partySize;

    const datesChanged =
      moment(values.dates.startDate).format('YYYY-MM-DD') !==
        moment(initialValues.dates.startDate).format('YYYY-MM-DD') ||
      moment(values.dates.endDate).format('YYYY-MM-DD') !==
        moment(initialValues.dates.endDate).format('YYYY-MM-DD');

    const newDatesFormatted = formatDatesForTransaction(values.dates, timezone);

    const hasCustomerPriceChanged = priceDifferenceForCustomer.amount !== 0;

    let formattedNewPartySize = values.partySize;

    if (typeof values.partySize === 'object') {
      formattedNewPartySize = values.partySize;
    } else {
      formattedNewPartySize = parseInt(values.partySize, 10);
    }

    const newLodgingDate = values?.dates?.startLodgingDate;
    const newLodgingDateFormatted = newLodgingDate
      ? format(newLodgingDate, 'dddd, MMMM D, YYYY')
      : null;
    const initialLodgingDate = initialValues?.dates?.startLodgingDate;
    const initialLodgingDateFormatted = initialLodgingDate
      ? format(initialLodgingDate, 'dddd, MMMM D, YYYY')
      : null;

    return {
      currentListing,
      currentProvider,
      currentUser,
      lineItems: updatedLineItems,
      newValues: {
        partySize: formattedNewPartySize,
        dates: {
          startDate: JSON.stringify(values.dates.startDate),
          endDate: JSON.stringify(values.dates.endDate),
          formattedStartDate: format(values.dates.startDate, 'dddd, MMMM D, YYYY'),
          formattedEndDate: format(values.dates.endDate, 'dddd, MMMM D, YYYY'),
          startLodgingDate: JSON.stringify(newLodgingDate),
          formattedStartLodgingDate: newLodgingDateFormatted,
        },
        days: daysBetween(newDatesFormatted.startDate, newDatesFormatted.endDate) + 1,
      },
      originalValues: {
        partySize: initialValues.partySize,
        dates: {
          startDate: JSON.stringify(initialValues.dates.startDate),
          endDate: JSON.stringify(initialValues.dates.endDate),
          formattedStartDate: format(initialValues.dates.startDate, 'dddd, MMMM D, YYYY'),
          formattedEndDate: format(initialValues.dates.endDate, 'dddd, MMMM D, YYYY'),
          startLodgingDate: JSON.stringify(initialLodgingDate),
          formattedStartLodgingDate: initialLodgingDateFormatted,
        },
      },
      partySizeChanged,
      datesChanged,
      createPayment: hasCustomerPriceChanged,
      clientSideConfirm: hasCustomerPriceChanged && !hasPaymentMethod,
    };
  };

  const handleSubmit = async (values, initialValues) => {
    setErrorMessage(null);

    if (needsInlinePayment) {
      return;
    }

    try {
      const params = buildSubmitParams(values, initialValues);
      await onSubmit(params, currentTransaction.id);
      window.scrollTo(0, 0);
    } catch (error) {
      console.log(error);
      setErrorMessage(intl.formatMessage({ id: 'BookingChanges.sendRequestError' }));
    }
  };

  const handleInlinePaymentConfirmed = async (values, initialValues) => {
    setErrorMessage(null);

    try {
      const params = buildSubmitParams(values, initialValues);

      const completeParams = {
        stripePaymentIntentId: paymentState.stripePaymentIntentId,
        customerTotal: paymentState.customerTotal,
        applicationTotal: paymentState.applicationTotal,
        changedTotals: paymentState.changedTotals,
        lineItems: params.lineItems,
        newValues: params.newValues,
        originalValues: params.originalValues,
        partySizeChanged: params.partySizeChanged,
        datesChanged: params.datesChanged,
      };

      await onCompleteRequestChanges(completeParams, currentTransaction.id);
      clearPaymentState();
      window.location.reload();
    } catch (error) {
      console.log(error);
      setErrorMessage(intl.formatMessage({ id: 'BookingChanges.sendRequestError' }));
    }
  };

  const handleAccept = async () => {
    try {
      setErrorMessage(null);
      await onAccept(currentTransaction.id);
      onClose();
    } catch (error) {
      console.log(error);
      setErrorMessage(intl.formatMessage({ id: 'BookingChanges.sendRequestError' }));
    }
  };

  const handleDecline = async () => {
    try {
      setErrorMessage(null);
      await onDecline(currentTransaction.id);
      onClose();
    } catch (error) {
      console.log(error);
      setErrorMessage(intl.formatMessage({ id: 'BookingChanges.sendRequestError' }));
    }
  };

  const pricingDetails = (
    <PricingDetails
      isCustomer={isCustomer}
      formattedCustomerOriginalPrice={formattedCustomerOriginalPrice}
      formattedNewCustomerPrice={formattedNewCustomerPrice}
      formattedPriceDifferenceForCustomer={formattedPriceDifferenceForCustomer}
      isProvider={isProvider}
      formattedNewProviderPrice={formattedNewProviderPrice}
      formattedProviderOriginalPrice={formattedProviderOriginalPrice}
      formattedPriceDifferenceForProvider={formattedPriceDifferenceForProvider}
    />
  );

  const inlinePayment = paymentState?.clientSecret
    ? ({ values, initialValues, submitDisabled }) => (
        <InlinePaymentElement
          clientSecret={paymentState.clientSecret}
          values={values}
          initialValues={initialValues}
          submitDisabled={submitDisabled}
          inProgress={inProgress || isCreatingPI}
          onPaymentConfirmed={handleInlinePaymentConfirmed}
        />
      )
    : null;

  const bookingDetailsForm = (
    <BookingDetails
      intl={intl}
      currentPackage={currentPackage}
      currentTransaction={currentTransaction}
      timeSlots={timeSlots}
      setNewGuestSize={setNewGuestSize}
      setNewBookingDates={setNewBookingDates}
      newPrice={newCustomerPrice}
      onSubmit={handleSubmit}
      inProgress={inProgress || isCreatingPI}
      hasCustomerRequestedChanges={hasCustomerRequestedChanges}
      onClose={onClose}
      onAccept={handleAccept}
      acceptChangesInProgress={acceptChangesInProgress}
      onDecline={handleDecline}
      declineChangesInProgress={declineChangesInProgress}
      isProvider={isProvider}
      errorMessage={combinedErrorMessage}
      onDatePickerOpenChange={setIsDatePickerOpen}
      inlinePaymentElement={inlinePayment}
      disableSubmit={isAwaitingInlinePayment}
    />
  );

  const bookingDetailsMobileForm = (
    <BookingDetails
      intl={intl}
      currentPackage={currentPackage}
      currentTransaction={currentTransaction}
      timeSlots={timeSlots}
      setNewGuestSize={setNewGuestSize}
      setNewBookingDates={setNewBookingDates}
      newPrice={newCustomerPrice}
      priceDifferenceForCustomer={priceDifferenceForCustomer}
      onSubmit={handleSubmit}
      inProgress={inProgress || isCreatingPI}
      hasCustomerRequestedChanges={hasCustomerRequestedChanges}
      onClose={onClose}
      onAccept={handleAccept}
      acceptChangesInProgress={acceptChangesInProgress}
      onDecline={handleDecline}
      declineChangesInProgress={declineChangesInProgress}
      isProvider={isProvider}
      pricingDetails={pricingDetails}
      errorMessage={combinedErrorMessage}
      onDatePickerOpenChange={setIsDatePickerOpen}
      inlinePaymentElement={inlinePayment}
      disableSubmit={isAwaitingInlinePayment}
    />
  );

  return (
    <div className={classNames(css.root, { [css.rootWithTooltip]: isDatePickerOpen })}>
      <div className={css.formContainer}>
        <div className={css.formContainerHeader}>
          <Header
            hasCustomerRequestedChanges={hasCustomerRequestedChanges}
            isProvider={isProvider}
            isCustomer={isCustomer}
            providedDisplayName={providedDisplayName}
            customerDisplayName={customerDisplayName}
          />
          <div className={css.divider} />

          {bookingDetailsForm}
        </div>
      </div>

      <div className={css.pricingDetailsContainer}>
        <div className={css.pricingDetailsMobileHeader}>
          <Header
            hasCustomerRequestedChanges={hasCustomerRequestedChanges}
            isProvider={isProvider}
            isCustomer={isCustomer}
            providedDisplayName={providedDisplayName}
            customerDisplayName={customerDisplayName}
          />
        </div>

        <TransactionListingHeading
          listing={currentListing}
          currentUser={currentUser}
          provider={currentTransaction?.provider}
          customer={currentTransaction?.customer}
          listingLink={listingLink}
          packageId={packageId}
          currentTransaction={currentTransaction}
          showTitleOnMobile
        />

        <div className={css.divider} />

        <div className={css.bookingDetailsMobile}>{bookingDetailsMobileForm}</div>

        <div className={css.priceDetailsDesktop}>{pricingDetails}</div>
      </div>
    </div>
  );
};

BookingChanges.defaultProps = {
  timeSlots: null,
  onCompleteRequestChanges: null,
  onCreatePaymentIntent: null,
};

BookingChanges.propTypes = {
  currentListing: propTypes.listing.isRequired,
  currentUser: propTypes.currentUser.isRequired,
  packageId: string.isRequired,
  currentTransaction: object.isRequired,
  timeSlots: arrayOf(propTypes.timeSlot),
  onSubmit: func.isRequired,
  onCompleteRequestChanges: func,
  onCreatePaymentIntent: func,
  inProgress: bool.isRequired,
  stateData: object.isRequired,
  onClose: func.isRequired,
  onAccept: func.isRequired,
  acceptChangesInProgress: bool.isRequired,
  onDecline: func.isRequired,
  declineChangesInProgress: bool.isRequired,
};

const BookingChangesWithIntl = compose(injectIntl)(BookingChanges);

// Wrap with error boundary
const BookingChangesWithErrorBoundary = props => {
  const { onClose } = props;

  return (
    <BookingChangesErrorBoundary onClose={onClose}>
      <BookingChangesWithIntl {...props} />
    </BookingChangesErrorBoundary>
  );
};

BookingChangesWithErrorBoundary.defaultProps = BookingChanges.defaultProps;
BookingChangesWithErrorBoundary.propTypes = BookingChanges.propTypes;
export default BookingChangesWithErrorBoundary;
